import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import LoadingButton from "../components/LoadingButton";
import { Plus, HandCoins, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatRupiah, formatDate } from "../lib/api";

export default function AdminPinjaman() {
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ member_id: "", amount: "", tenor_months: 12, interest_rate: 1.0, purpose: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState("");

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([
        api.get("/admin/pinjaman"),
        api.get("/admin/members", { params: { status: "active" } }),
      ]);
      setRows(p.data);
      setMembers(m.data);
    } catch (e) { setError(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/admin/pinjaman", { ...form, amount: parseFloat(form.amount), tenor_months: parseInt(form.tenor_months), interest_rate: parseFloat(form.interest_rate) });
      toast.success("Pinjaman berhasil dicatat");
      setOpen(false);
      setForm({ member_id: "", amount: "", tenor_months: 12, interest_rate: 1.0, purpose: "" });
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const complete = async (id) => {
    setCompleting(id);
    try {
      await api.post(`/admin/pinjaman/${id}/complete`);
      toast.success("Pinjaman ditutup (lunas)");
      load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setCompleting(""); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">Pinjaman</h1>
            <p className="text-brand-muted text-sm mt-1">Pencatatan dan pengelolaan pinjaman anggota.</p>
          </div>
          <button onClick={() => setOpen(true)} data-testid="open-pinjaman-form-btn" className="rounded-full bg-brand text-white px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-brand-hover transition">
            <Plus size={14} /> Pinjaman Baru
          </button>
        </div>

        {error && <div className="rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3">{error}</div>}

        <div className="bg-white rounded-3xl border border-brand/10 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pinjaman-table">
              <thead className="bg-brand-soft/60 text-left text-brand-muted">
                <tr>
                  <th className="px-4 py-3">Tanggal</th>
                  <th className="px-4 py-3">Anggota</th>
                  <th className="px-4 py-3 text-right">Plafon</th>
                  <th className="px-4 py-3 text-right">Terbayar</th>
                  <th className="px-4 py-3 text-right">Sisa</th>
                  <th className="px-4 py-3">Tenor</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-brand-muted">Belum ada pinjaman</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} className="border-t border-brand/10 hover:bg-brand-soft/30">
                    <td className="px-4 py-3">{formatDate(r.applied_at)}</td>
                    <td className="px-4 py-3"><div className="font-medium text-brand-ink">{r.member_name}</div><div className="text-xs text-brand-muted">{r.member_number}</div></td>
                    <td className="px-4 py-3 text-right">{formatRupiah(r.amount)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600">{formatRupiah(r.paid_amount)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-brand-ink">{formatRupiah(r.remaining_amount)}</td>
                    <td className="px-4 py-3">{r.tenor_months} bln</td>
                    <td className="px-4 py-3">
                      <span className={"inline-flex items-center gap-1 rounded-full text-xs px-2.5 py-1 " + (r.status === "active" ? "bg-amber-100 text-amber-700" : r.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600")}>
                        <HandCoins size={12} /> {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === "active" && r.remaining_amount <= 0.01 && (
                        <LoadingButton onClick={() => complete(r.id)} loading={completing === r.id} loadingText="Menutup…" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-3 py-1.5" data-testid={`complete-pinjaman-${r.id}`}>
                          <CheckCircle2 size={12} /> Tutup
                        </LoadingButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="bg-white rounded-3xl w-full sm:max-w-md p-5 sm:p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="pinjaman-form">
            <div className="font-heading font-bold text-lg text-brand-ink">Pinjaman Baru</div>
            <Field label="Anggota">
              <select required value={form.member_id} onChange={(e) => setForm((p) => ({ ...p, member_id: e.target.value }))} className="inp" data-testid="pinjaman-member-select">
                <option value="">— Pilih anggota —</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.member_number ? `[${m.member_number}] ` : ""}{m.full_name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Plafon (Rp)"><input type="number" required min="0" className="inp" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="pinjaman-amount-input" /></Field>
              <Field label="Tenor (bln)"><input type="number" min="1" required className="inp" value={form.tenor_months} onChange={(e) => setForm((p) => ({ ...p, tenor_months: e.target.value }))} data-testid="pinjaman-tenor-input" /></Field>
              <Field label="Bunga / bln (%)"><input type="number" step="0.01" min="0" className="inp" value={form.interest_rate} onChange={(e) => setForm((p) => ({ ...p, interest_rate: e.target.value }))} data-testid="pinjaman-rate-input" /></Field>
            </div>
            <Field label="Keperluan"><textarea className="inp h-20" value={form.purpose} onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))} data-testid="pinjaman-purpose-input" /></Field>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} disabled={submitting} className="rounded-full border border-brand/20 px-4 py-2 text-sm disabled:opacity-60">Batal</button>
              <LoadingButton type="submit" loading={submitting} loadingText="Menyimpan…" data-testid="pinjaman-submit-btn" className="rounded-full bg-brand text-white px-5 py-2 text-sm hover:bg-brand-hover">Simpan</LoadingButton>
            </div>
            <style>{`.inp{width:100%;border-radius:12px;border:1px solid rgba(11,110,110,0.2);padding:9px 12px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-brand-ink mb-1.5 uppercase tracking-[0.16em]">{label}</div>
      {children}
    </label>
  );
}
